import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Loader2, Minus, Plus, Trash2, Package } from 'lucide-react';
import { useTheme } from '@evi/skins';
import { countChargerUnits, BULK_QUOTE_MIN_CHARGER_UNITS } from '@evi/shared';
import { createStoreCheckout, fetchStoreCatalog, formatCents } from '@/lib/api';
import { useCartStore, selectSubtotalCents } from '@/store/cart';
import { cartItemImage, shippingLineFor } from './storeCatalog';
import { EvieOrb } from '@/components/primitives';
import { useEvieChatStore } from '@/store/evieChat';
import styles from './Store.module.css';

interface Props { onNavigate: (path: string) => void }

export function CartPage({ onNavigate }: Props) {
  const { t } = useTranslation('storeCheckout');
  const { mode } = useTheme();
  const items = useCartStore((s) => s.items);
  const setQty = useCartStore((s) => s.setQty);
  const remove = useCartStore((s) => s.remove);
  const subtotalCents = useCartStore(selectSubtotalCents);
  const [checkingOut, setCheckingOut] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  // 4+ charger carts still check out (policy change); we only surface a note
  // that our team confirms final freight and logistics after the order.
  // Charger units are counted the same way the server does (accessories
  // excluded).
  const chargerUnits = countChargerUnits(
    items.map((i) => ({ part_number: i.part_number, name: i.name, tier: i.tier ?? '', quantity: i.qty })),
  );
  const isBulk = chargerUnits >= BULK_QUOTE_MIN_CHARGER_UNITS;

  // Row thumbnails resolve through the same catalog family mapping the
  // cards use (cartItemImage). Usually a cache hit from the catalog page;
  // until it loads (or if a product is gone) rows render without a thumb.
  const catalogQ = useQuery({
    queryKey: ['store', 'catalog'],
    queryFn: fetchStoreCatalog,
    staleTime: 30_000,
    enabled: items.length > 0,
  });
  const catalog = catalogQ.data ?? [];

  const checkout = async (): Promise<void> => {
    if (checkingOut || items.length === 0) return;
    setCheckingOut(true);
    setCheckoutError(null);
    try {
      const { url } = await createStoreCheckout(
        items.map((i) => ({ product_id: i.product_id, quantity: i.qty })),
      );
      // Full-page redirect to Stripe's hosted page. Cancel returns to this
      // cart; success lands on /store/success with the session id.
      window.location.href = url;
    } catch {
      setCheckoutError(t('cart.checkoutError'));
      setCheckingOut(false);
    }
  };

  return (
    <div className={styles.store} data-store-theme={mode}>
      <header className={`${styles.head} ${styles.headSolid}`}>
        <button type="button" className={styles.headBtn} aria-label={t('cart.back')} onClick={() => onNavigate('/store')}>
          <ArrowLeft size={18} />
        </button>
        <span className={`${styles.headTitle} ${styles.headTitleShown}`}>
          {t('cart.headerTitle')}{items.length > 0 ? ` · ${items.length}` : ''}
        </span>
        {/* My Orders only — the cart icon is redundant on the cart page. */}
        <span className={styles.headActions}>
          <button
            type="button"
            className={styles.headBtn}
            aria-label={t('cart.askEvii')}
            onClick={() => {
              useEvieChatStore.getState().setContext({ surface: 'store' });
              useEvieChatStore.getState().openSheet();
            }}
          >
            <EvieOrb size={18} />
          </button>
          <button type="button" className={styles.headBtn} aria-label={t('cart.myOrders')} onClick={() => onNavigate('/store/orders')}>
            <Package size={18} />
          </button>
        </span>
      </header>

      <div className={styles.body}>
        {items.length === 0 ? (
          <div className={styles.emptyBox}>
            <p>{t('cart.emptyTitle')}</p>
            <button type="button" className={styles.sheetAdd} onClick={() => onNavigate('/store')}>{t('cart.browse')}</button>
          </div>
        ) : (
          <>
            <div className={styles.cartList}>
              {items.map((item) => {
                const img = cartItemImage(item.product_id, catalog);
                return (
                <div key={item.product_id} className={styles.cartItem}>
                  {img && (
                    <img
                      className={styles.cartThumb}
                      src={img}
                      alt=""
                      aria-hidden
                      loading="lazy"
                    />
                  )}
                  <div className={styles.cartItemInfo}>
                    <span className={styles.cartItemName}>{item.name}</span>
                    <span className={styles.cartItemUnit}>{t('cart.each', { price: formatCents(item.unit_price_cents) })}</span>
                    <span className={styles.cartItemShip}>{shippingLineFor(item)}</span>
                  </div>
                  <div className={styles.qtyStepper}>
                    <button type="button" className={styles.qtyBtn} aria-label={t('cart.decrease')} onClick={() => setQty(item.product_id, item.qty - 1)}>
                      <Minus size={14} />
                    </button>
                    <span className={styles.qtyVal}>{item.qty}</span>
                    <button type="button" className={styles.qtyBtn} aria-label={t('cart.increase')} onClick={() => setQty(item.product_id, item.qty + 1)}>
                      <Plus size={14} />
                    </button>
                  </div>
                  <button type="button" className={styles.removeBtn} aria-label={t('cart.remove')} onClick={() => remove(item.product_id)}>
                    <Trash2 size={16} />
                  </button>
                </div>
                );
              })}
            </div>

            <div className={styles.subtotalRow}>
              <span className={styles.subtotalLabel}>{t('cart.subtotal')}</span>
              <span className={styles.subtotalVal}>
                {formatCents(subtotalCents)} <span className={styles.usd}>USD</span>
              </span>
            </div>

            <button
              type="button"
              className={styles.checkoutBtn}
              disabled={checkingOut}
              onClick={checkout}
            >
              {checkingOut
                ? <><Loader2 size={15} className={styles.checkoutSpin} /> {t('cart.opening')}</>
                : t('cart.checkout')}
            </button>
            {checkoutError && <div className={styles.errorBox}>{checkoutError}</div>}
            {isBulk && (
              <p className={styles.checkoutNote}>
                {t('cart.bulkNote')}
              </p>
            )}
            <p className={styles.checkoutNote}>
              {t('cart.secureNote')}
            </p>

            <p className={styles.checkoutNote}>
              {t('cart.internationalNote')}
            </p>
            <p className={styles.checkoutNote}>
              {t('cart.shippingNote')}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
